import type { ReactNode } from 'react';
import type { GameDto } from '@/queries/games/games.schema';
import { SlotsFilledAliases } from '@/consts/seo/SlotsAliases';
import { isStaging } from '@/libs/getEnvironment';
import { parseBlocksValue } from '../../utils/detailed-game.utils';

export const COMPARE_GAME_LIST = isStaging() ? ['le-cowboy', 'best-game', 'super-50-stars1', 'china-pots-test-seo'] : SlotsFilledAliases;
const LINK_VALUE_KEYS = new Set<keyof GameDetailsInfo>(['software', 'features', 'themes']);

type GameDetailsInfo = NonNullable<GameDto['details']>;

type CompareRow = {
  key: keyof GameDetailsInfo | 'rating';
  labelKey: string;
};

export const compareRows: CompareRow[] = [
  // TODO: add rating to game details and remove hardcoded rating row when it's available
  // { key: 'rating', labelKey: 'game-details.compare-rows.rating' },
  { key: 'software', labelKey: 'game-details.compare-rows.software' },
  { key: 'releaseDate', labelKey: 'game-details.compare-rows.release-date' },
  { key: 'maxBet', labelKey: 'game-details.compare-rows.max-play' },
  { key: 'minBet', labelKey: 'game-details.compare-rows.min-play' },
  { key: 'rtp', labelKey: 'game-details.compare-rows.rtp' },
  { key: 'volatility', labelKey: 'game-details.compare-rows.volatility' },
  { key: 'bonusRound', labelKey: 'game-details.compare-rows.bonus-round' },
  { key: 'autoplay', labelKey: 'game-details.compare-rows.autoplay' },
  { key: 'freeSpins', labelKey: 'game-details.compare-rows.free-spins' },
  { key: 'multiplayer', labelKey: 'game-details.compare-rows.multiplier' },
  { key: 'wild', labelKey: 'game-details.compare-rows.wild' },
  { key: 'scatter', labelKey: 'game-details.compare-rows.scatter' },
  { key: 'reels', labelKey: 'game-details.compare-rows.reels' },
  { key: 'paylines', labelKey: 'game-details.compare-rows.paylines' },
  { key: 'totalMaxWin', labelKey: 'game-details.compare-rows.total-max-win' },
];

export const hashString = (value: string) => {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = ((hash << 5) - hash) + value.charCodeAt(index);
    hash |= 0;
  }

  return Math.abs(hash);
};

export const getCompareGameAliases = (game?: Pick<GameDto, 'alias' | 'id'>) => {
  if (!game?.id) {
    return [];
  }

  return COMPARE_GAME_LIST
    .filter(alias => alias !== game.alias)
    .sort((first, second) => hashString(`${game.id}-${first}`) - hashString(`${game.id}-${second}`))
    .slice(0, 3);
};

export const hasComparableDetails = (details?: GameDetailsInfo | null) =>
  Boolean(details && Object.values(details).some(value => value !== undefined && value !== null && value !== ''));

const parseLinkValue = (value: string) => {
  const trimmedValue = value.trim();

  if (!trimmedValue.startsWith('[')) {
    return value;
  }

  const parsedBlocks = parseBlocksValue(trimmedValue);
  const textValue = parsedBlocks
    .map(block => (block.type === 'text' ? block.content : block.content.text))
    .join(' ')
    .trim();

  return textValue || '—';
};

export const getCellValue = (game: GameDto, rowKey: CompareRow['key']): ReactNode => {
  const value = game.details?.[rowKey as keyof GameDetailsInfo];

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (typeof value === 'string' && value.length > 0) {
    if (rowKey !== 'rating' && LINK_VALUE_KEYS.has(rowKey)) {
      return parseLinkValue(value);
    }

    return value.trim() || '—';
  }

  return '—';
};
